import { useCallback, useContext, type ReactNode } from 'react';

// Import the context directly to check if we're inside a provider
// This avoids the throwing behavior of usePanelContext
import { isMobileHeaderSheetOpenAtom } from '@/app-shell/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { PanelContext } from '@heidi/design-system/page-shell';
import { useIsMobile } from '@heidi/utils';
import { useSetAtom } from 'jotai';

/**
 * Hook for opening and closing mobile panel navigation.
 *
 * Safe to call outside of a PanelProvider - returns no-op functions when
 * there's no provider (e.g., on desktop where NavItem is used without
 * the mobile PanelProvider wrapper).
 *
 * @example
 * ```tsx
 * const { openPanel, closePanel, isInMobileProvider } = useMobilePanel();
 *
 * // Open panel with content (only works when inside PanelProvider)
 * if (isInMobileProvider) {
 *   openPanel(<SessionsPanelContent />, <SessionsPanelAction />);
 * }
 * ```
 */
export function useMobilePanel() {
  const context = useContext(PanelContext);

  const openPanel = useCallback(
    (content: ReactNode, action?: ReactNode) => {
      if (context) {
        context.setActivePanelContent(content);
        context.setActivePanelAction(action ?? null);
        context.setMobileView('panel');
      }
    },
    [context]
  );

  const closePanel = useCallback(() => {
    if (context) {
      context.setMobileView('sidebar');
    }
  }, [context]);

  const isMobile = useIsMobile();
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);

  const dismissMobileSidebar = useCallback(() => {
    if (isMobile) {
      closePanel();
      setIsMobileHeaderSheetOpen(false);
    }
  }, [isMobile, closePanel, setIsMobileHeaderSheetOpen]);

  return {
    openPanel,
    closePanel,
    dismissMobileSidebar,
    /** Whether the hook is being used inside a PanelProvider */
    isInMobileProvider: !!context,
  };
}
